/**
 * Sesión del usuario y del dispositivo.
 *
 * Dos cosas distintas viven aquí, con vidas distintas a propósito:
 *
 *   · El TOKEN DE SESIÓN vence en horas y se borra al cerrar sesión. Es de la
 *     persona.
 *   · El TOKEN DE DISPOSITIVO no vence y se queda en la tablet para siempre.
 *     Es del aparato. Gracias a él, al cambiar de turno el cocinero sólo
 *     teclea su PIN en vez de dar de alta el aparato otra vez.
 */
export type Rol = 'dueno' | 'cajero' | 'cocina' | 'mesero' | 'repartidor';

export interface Usuario {
  id: number;
  nombre: string;
  usuario: string;
  rol: Rol;
  sucursales: number[];
}

const LLAVE_SESION = 'ericmar.sesion';
const LLAVE_DISPOSITIVO = 'ericmar.dispositivo';
const LLAVE_SUCURSAL = 'ericmar.sucursal';

type Escucha = (usuario: Usuario | null) => void;

function leer<T>(llave: string): T | null {
  try {
    const crudo = localStorage.getItem(llave);
    return crudo ? (JSON.parse(crudo) as T) : null;
  } catch {
    // Modo privado, almacenamiento lleno o datos corruptos: la app sigue
    // funcionando, sólo pidiendo login otra vez.
    return null;
  }
}

function escribir(llave: string, valor: unknown) {
  try {
    localStorage.setItem(llave, JSON.stringify(valor));
  } catch {
    /* sin persistencia; la sesión vive sólo en memoria */
  }
}

class Sesion {
  token: string | null = null;
  usuario: Usuario | null = null;
  sucursalId: number | null = null;
  private escuchas = new Set<Escucha>();

  constructor() {
    const guardada = leer<{ token: string; usuario: Usuario }>(LLAVE_SESION);
    if (guardada) {
      this.token = guardada.token;
      this.usuario = guardada.usuario;
    }
    this.sucursalId = leer<number>(LLAVE_SUCURSAL);
  }

  get tokenDispositivo(): string | null {
    return leer<string>(LLAVE_DISPOSITIVO);
  }

  guardarDispositivo(token: string) {
    escribir(LLAVE_DISPOSITIVO, token);
  }

  abrir(token: string, usuario: Usuario) {
    this.token = token;
    this.usuario = usuario;
    // Si sólo tiene una sucursal, no tiene caso preguntarle cuál.
    if (!this.sucursalId || !usuario.sucursales.includes(this.sucursalId)) {
      this.elegirSucursal(usuario.sucursales[0] ?? null);
    }
    escribir(LLAVE_SESION, { token, usuario });
    this.avisar();
  }

  elegirSucursal(id: number | null) {
    this.sucursalId = id;
    if (id) escribir(LLAVE_SUCURSAL, id);
    this.avisar();
  }

  cerrar() {
    this.token = null;
    this.usuario = null;
    try {
      localStorage.removeItem(LLAVE_SESION);
    } catch {
      /* nada que limpiar */
    }
    // El token del dispositivo NO se borra: la tablet sigue dada de alta.
    this.avisar();
  }

  suscribir(fn: Escucha): () => void {
    this.escuchas.add(fn);
    return () => {
      this.escuchas.delete(fn);
    };
  }

  private avisar() {
    for (const fn of this.escuchas) fn(this.usuario);
  }
}

export const sesion = new Sesion();
